import List from '@pelckmans/elementis/components/list';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoutes } from '../../hooks/useRoutes';
import LoadingState from '../LoadingState';
import NoContent from '../NoContent';
import type { ContentVariant } from './ShareModal';
import { ShareModalGroupItem } from './ShareModalGroupItem';
import type { MemberWithGroup } from './useMemberSearch';
import { useShareModalContext } from './useShareModalContext';

const BASE = 'PAGES.TOC_LEVEL.MODALS.SHARE';

interface ShareModalBodyProps {
  contentVariant: ContentVariant;
  containsNonScorable: boolean;
  containsNonShareableAsAssignment: boolean;
  isSearching: boolean;
  searchResults: MemberWithGroup[];
  onClearSearch?: () => void;
}

export function ShareModalBody({
  contentVariant,
  containsNonScorable,
  containsNonShareableAsAssignment,
  isSearching,
  searchResults,
  onClearSearch,
}: ShareModalBodyProps) {
  const { t } = useTranslation();
  const { groupsBySchool, membersByGroup, isLoading, selectedGroups, selectedMembers } = useShareModalContext();
  const { buildAbsolutePath } = useRoutes();

  const [collapsedSchools, setCollapsedSchools] = useState<string[]>([]);

  const handleCollapseToggle = (schoolName: string) => {
    setCollapsedSchools((current: string[]) =>
      current.includes(schoolName) ? current.filter(name => name !== schoolName) : [...current, schoolName],
    );
  };

  if (isLoading) {
    return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />;
  }

  switch (contentVariant) {
    case 'no-groups':
      return (
        <NoContent title={t(`${BASE}.NO_CONTENT.NO_GROUPS.TITLE`)}>
          <span
            dangerouslySetInnerHTML={{
              __html: t(`${BASE}.NO_CONTENT.NO_GROUPS.TEXT`, { url: buildAbsolutePath(['MY_GROUPS']) }),
            }}
          />
        </NoContent>
      );
    case 'no-filter-result':
      return (
        <NoContent title={t(`${BASE}.NO_CONTENT.NO_FILTER_RESULT.TITLE`)} onClearFiltersClick={onClearSearch}>
          {t(`${BASE}.NO_CONTENT.NO_FILTER_RESULT.TEXT`)}
        </NoContent>
      );
    case 'pelckmans-slide-set':
      return (
        <NoContent title={t(`${BASE}.NO_CONTENT.PELCKMANS_SLIDE_SETS.TITLE`)}>
          {t(`${BASE}.NO_CONTENT.PELCKMANS_SLIDE_SETS.TEXT`)}
        </NoContent>
      );
    default:
      return (
        <List>
          {Object.entries(groupsBySchool).map(([schoolName, groups]) => {
            // Filter out the entire school when searching if none of its groups contain results
            if (
              isSearching &&
              !groups.some(group =>
                (membersByGroup[group.id] || []).some(m =>
                  searchResults.some(r => r.id === m.id && r.groupId === group.id),
                ),
              )
            ) {
              return null;
            }

            return (
              <Fragment key={schoolName}>
                <List.Item
                  key={schoolName}
                  collapsed={collapsedSchools.includes(schoolName)}
                  title={schoolName}
                  collapseButtonLabelText={t(`${BASE}.ACTIONS.SCHOOL_COLLAPSE_BUTTON_LABEL`)}
                  level={0}
                  onToggleCollapse={() => handleCollapseToggle(schoolName)}
                />
                {!collapsedSchools.includes(schoolName) &&
                  groups.map(group => {
                    let members = membersByGroup[group.id];

                    if (isSearching) {
                      members = members.filter(m => searchResults.some(r => r.id === m.id && r.groupId === group.id));

                      if (!members?.length) return null;
                    }

                    return (
                      <ShareModalGroupItem
                        key={group.id}
                        group={group}
                        containsNonScorable={containsNonScorable}
                        containsNonShareableAsAssignment={containsNonShareableAsAssignment}
                        members={members}
                        selectedGroup={selectedGroups.find(g => g.groupId === group.id)}
                        selectedMembers={selectedMembers.filter(m => m.groupId === group.id)}
                      />
                    );
                  })}
              </Fragment>
            );
          })}
        </List>
      );
  }
}
